import { useEffect, useState } from 'react';
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step
} from 'react-joyride';
import { useQuery } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Route, Routes } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { useConfiguration } from 'contexts/country-config';
import { FeatureFlagsProvider } from 'contexts/feature-flags';
import { SnackbarProvider } from 'notistack';
import apiService from 'services/api';
import { AppRoute } from 'share/types/routes';
import getFeatureFlags from 'utils/get-feature-flags';

import { AllowanceContainer } from 'components/Allowance';
import { CostCenterContainer } from 'components/CostCenter';
import { DashboardContainer } from 'components/Dashboard';
import { DepartmentContainer } from 'components/Department';
import { EmployeeContainer } from 'components/Employee';
import { ExpenseCodeContainer } from 'components/ExpenseCode';
import { FtueTooltip } from 'components/FtueTooltip/FtueTooltip';
import { GiftCardContainer } from 'components/GiftCard';
import { Layout } from 'components/Layout';
import { LoginContainer } from 'components/Login';
import NoMatch from 'components/NoMatch';
import { ReportContainer } from 'components/Report';
import { SubscriptionContainer } from 'components/Subscription';
import { getTheme } from 'components/Theme/theme';
import { BrandCodes } from 'components/Theme/types';
import { UpgradePlanContainer } from 'components/UpgradePlan';

import ResetPassword from './components/ForgotPassword';
import { LocationContainer } from './components/Location';
import { ProtectedRoute } from './components/ProtectedRoute';
import SetPassword from './components/SetPassword';
import { TwoFactorAuthenticationContainer } from './components/TwoFactorAuthentication';
import { useAuth } from './contexts/authentication';
import User from './types/user';
import { FeatureFlag } from './utils/constants';

export function App() {
  const {
    api_host,
    geolocator_baseUrl,
    code,
    entity: { geid, code: entityCode }
  } = useConfiguration();

  const auth = useAuth();
  const feature_flags = getFeatureFlags(code);

  const [tourActive, setTourActive] = useState(false);
  const [showTour, setShowTour] = useState<boolean>(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [placement, setPlacement] = useState('right');
  const [isCsr, setIsCsr] = useState(false);

  // to control render of CSR components (such as FTUE joyride component)
  useEffect(() => {
    setIsCsr(true);
  }, []);

  const theme = getTheme(entityCode.toLowerCase() as BrandCodes);
  const api = apiService(api_host, geolocator_baseUrl, geid);

  const getUserQuery = useQuery<User, Error>(
    'user',
    async () => {
      return await api.user.get();
    },
    {
      enabled: Boolean(auth.token)
    }
  );

  const user = getUserQuery.data;

  const isVerified =
    user == undefined ? false : user.company.is_company_management_enabled;

  // gift card flag controlled by BE for whitelisted production testing
  feature_flags[FeatureFlag.GiftCard] =
    user?.company.is_gift_card_enabled ?? feature_flags[FeatureFlag.GiftCard];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, status, type, index } = data;

    if (index == 2 && action == ACTIONS.UPDATE) {
      setPlacement('bottom');
    }

    if (index == 3 && action == ACTIONS.UPDATE) {
      setPlacement('right');
    }

    if (
      ((action == ACTIONS.CLOSE && status == STATUS.FINISHED) ||
        (action == ACTIONS.SKIP && status == STATUS.SKIPPED)) &&
      type == EVENTS.TOUR_END
    ) {
      setTourActive(false);
      setShowTour(false);
    }
  };

  return (
    <div className="App">
      <FeatureFlagsProvider value={feature_flags}>
        <ThemeProvider theme={theme}>
          <SnackbarProvider>
            <Routes>
              <Route path={AppRoute.Login} element={<LoginContainer />} />
              <Route
                path={AppRoute.TwoFactorAuth}
                element={<TwoFactorAuthenticationContainer />}
              />
              <Route
                path={AppRoute.ForgotPassword}
                element={<ResetPassword />}
              />
              <Route path={AppRoute.SetPassword} element={<SetPassword />} />
              <Route
                path={AppRoute.Index}
                element={
                  <Layout
                    api={api}
                    isFullWidth={!isVerified}
                    handleTourActive={(run: boolean) => setTourActive(run)}
                    handleShowTour={(show: boolean) => setShowTour(show)}
                    showTour={showTour}
                    setSteps={(steps: Step[]) => setSteps(steps)}
                  />
                }
              >
                <Route index element={<DashboardContainer />} />
                <Route
                  path={AppRoute.Employee}
                  element={
                    <ProtectedRoute enabled={isVerified}>
                      <EmployeeContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.Department}
                  element={
                    <ProtectedRoute enabled={isVerified}>
                      <DepartmentContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.Allowance}
                  element={
                    <ProtectedRoute
                      enabled={
                        user == undefined
                          ? false
                          : isVerified && user.company.is_allowance_enabled
                      }
                    >
                      <AllowanceContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.Location}
                  element={
                    <ProtectedRoute enabled={isVerified}>
                      <LocationContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.Subscription}
                  element={
                    <ProtectedRoute
                      enabled={
                        user == undefined
                          ? false
                          : isVerified && user.company.is_subscription_enabled
                      }
                    >
                      <SubscriptionContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.Report}
                  element={
                    <ProtectedRoute enabled={isVerified}>
                      <ReportContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.ExpenseCode}
                  element={
                    <ProtectedRoute enabled={isVerified}>
                      <ExpenseCodeContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.CostCenter}
                  element={
                    <ProtectedRoute enabled={isVerified}>
                      <CostCenterContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.GiftCard}
                  element={
                    <ProtectedRoute
                      enabled={user?.company.is_gift_card_enabled || false}
                    >
                      <GiftCardContainer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.UpgradeAccount}
                  element={
                    <ProtectedRoute enabled={!isVerified}>
                      <UpgradePlanContainer />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<NoMatch />} />
            </Routes>
            {isCsr && (
              <Joyride
                run={tourActive}
                floaterProps={{ hideArrow: true }}
                scrollDuration={500}
                callback={handleJoyrideCallback}
                spotlightPadding={0}
                tooltipComponent={(props) => (
                  <FtueTooltip
                    stepSizes={[3, 3, 2, 2]}
                    placement={placement}
                    {...props}
                  />
                )}
                steps={steps}
                continuous={true}
                showProgress={true}
                showSkipButton
                disableOverlayClose
                disableCloseOnEsc
                disableScrollParentFix={true}
                scrollOffset={85}
                styles={{
                  options: {
                    zIndex: 1500,
                    overlayColor: 'rgba(51, 51, 51, 0.56)'
                  },
                  spotlight: { borderRadius: '8px' }
                }}
              />
            )}
          </SnackbarProvider>
        </ThemeProvider>
      </FeatureFlagsProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </div>
  );
}
